import { request } from '../client';
import { endpoints } from '../endpoints';

const clean = (v) => (v === undefined || v === null || v === '' ? undefined : v);

/**
 * Campaigns with their figures in ONE call (GET /reports/campaigns). Newest campaigns first.
 *  - `status`: active | draft | closed (left out = every status); `search`: part of the name; `page` starts at 1, `size` is 1-100 (default 20);
 *  - `from` / `to`: YYYY-MM-DD, both inclusive, counting respondents who ENTERED in that period; `tz`: an IANA time zone name (default UTC).
 * Resolves { items, total, page, size, totalPages, totals, filters }. Each item has the same figures as GET /project/{id}/statistics plus
 * name, status, dates and `lastActivityAt`; the figures only a respondent session can give (inSurvey, timedOut, entries, drop-out and
 * conversion rate, median length) are null when `source` is "redirects". `totals` covers the WHOLE filtered set, not the page: counts are
 * added up and rates recalculated from the added counts (never averaged); `totals.campaignsWithSessions` says how many campaigns have sessions.
 * Invalid filters are a 400 whose `errors` list every problem. A 404 means the backend has no such route yet (see ReportsPage's fallback).
 * @param {{ status?: string, search?: string, page?: number, size?: number, from?: string, to?: string, tz?: string }} [filters]
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const listCampaignReport = async ({ status, search, page = 1, size = 20, from, to, tz } = {}, opts) => {
  const raw = await request(endpoints.reports.campaigns(), {
    ...opts,
    query: { status: clean(status), search: clean(search), page, size, from: clean(from), to: clean(to), tz: from || to ? clean(tz) : undefined },
  });
  const items = (raw && raw.content) || [];
  return {
    items,
    total: raw && typeof raw.totalElements === 'number' ? raw.totalElements : items.length,
    page: raw && typeof raw.page === 'number' ? raw.page : page,
    size: raw && typeof raw.size === 'number' ? raw.size : size,
    totalPages: raw && typeof raw.totalPages === 'number' ? raw.totalPages : 1,
    totals: (raw && raw.totals) || null,
    filters: (raw && raw.filters) || null,
  };
};

/**
 * One campaign's figures over time (GET /project/{id}/statistics/timeline).
 *  - `interval`: day (default), week (weeks start on Monday) or month; `from` / `to` / `tz` as above; `targetGroupId`: only that target group
 *    (respondent tracking only).
 * Resolves { projectId, source, interval, tz, from, to, targetGroupId, points: [{ period, entries, complete, screenout, quotaFull,
 * qualityTerminate, timedOut }] }, or null when the campaign is not found or not yours (404, like the other campaign endpoints).
 * `period` is the first day of the bucket. `entries` is counted on the day the respondent entered; a final status on the day it was got
 * (a reconciled respondent counts under the current status). Days with no activity are included as zeros between the first and the last
 * activity (or from / to). For a campaign with no sessions (`source` "redirects") only complete, screenout and quotaFull are returned.
 * @param {string} projectId
 * @param {{ interval?: 'day' | 'week' | 'month', from?: string, to?: string, tz?: string, targetGroupId?: string }} [options]
 * @param {{ signal?: AbortSignal }} [opts]
 */
export const getStatisticsTimeline = async (projectId, { interval, from, to, tz, targetGroupId } = {}, opts) => {
  try {
    return await request(endpoints.projects.timeline(projectId), {
      ...opts,
      query: { interval: clean(interval), from: clean(from), to: clean(to), tz: clean(tz), targetGroupId: clean(targetGroupId) },
    });
  } catch (err) {
    if (err && err.status === 404) return null;
    throw err;
  }
};
